import { useQueryClient } from '@tanstack/react-query';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { makePortalUserNotificationsChannel, SocketEvent } from '@furality/centrifugo-types';
import { useAuth } from '@furality/frontend-stack';
import MessageTypes, {
  isToastPacket,
  isUserDisplayNamePacket,
  isUserRequestJoinPartyPacket,
} from '@/components/socket/SocketMessages.ts';
import useChannelEventSubscription from '@/lib/hooks/useChannelEventSubscription.ts';
import QUERY_KEYS from '@/lib/QUERY_KEYS';
import useToasts from '@/lib/useToast';

const AuthenticatedListener: FC<{ userId: string }> = ({ userId }) => {
  const channel = makePortalUserNotificationsChannel(userId);
  const { pushToast, pushPartyToast } = useToasts();
  const { t } = useTranslation();
  const qc = useQueryClient();

  useChannelEventSubscription(channel, SocketEvent.InstanceInviteSent, () => {
    pushToast('info', t('socket.invite_sent'));
  });

  useChannelEventSubscription(channel, SocketEvent.UserRequestJoinParty, (data: unknown) => {
    if (isUserRequestJoinPartyPacket(data)) {
      pushPartyToast(data.requesterVrchatId, data.requesterDisplayName);
    }
  });

  useChannelEventSubscription(channel, SocketEvent.UserJoinedParty, async (data: unknown) => {
    await qc.refetchQueries({ queryKey: [QUERY_KEYS.Party.Party] });
    if (isUserDisplayNamePacket(data)) {
      pushToast('info', t('socket.user_joined_party', { name: data.userDisplayName }));
    }
  });

  useChannelEventSubscription(channel, SocketEvent.UserLeftParty, async (data: unknown) => {
    await qc.refetchQueries({ queryKey: [QUERY_KEYS.Party.Party] });
    if (isUserDisplayNamePacket(data)) {
      pushToast('info', t('socket.user_left_party', { name: data.userDisplayName }));
    }
  });

  useChannelEventSubscription(channel, SocketEvent.JoinedParty, async () => {
    await qc.refetchQueries({ queryKey: [QUERY_KEYS.Party.Party] });
    pushToast('info', t('socket.accepted_into_party'));
  });

  useChannelEventSubscription(channel, SocketEvent.UserNotInFuralityConventionGroup, () => {
    pushToast('error', t('socket.not_convention_group_member'));
  });

  useChannelEventSubscription(channel, SocketEvent.SquadStateChanged, () => {
    qc.refetchQueries({ queryKey: [QUERY_KEYS.PrivateParty.Squad] });
  });

  useChannelEventSubscription(channel, MessageTypes.ReloadApplication, () => {
    console.warn('Reload triggered, refresh pending');
    setTimeout(
      () => {
        window.location.reload();
      },
      Math.floor(Math.random() * 5000), // Random timeout of up to 5 seconds
    );
  });

  useChannelEventSubscription(channel, MessageTypes.ReloadApplicationForce, () => {
    window.location.reload();
  });

  // For silly, generally don't need to send toasts to users
  useChannelEventSubscription(channel, SocketEvent.Toast, (data) => {
    if (isToastPacket(data)) {
      pushToast(data.type, data.message, data.duration);
    }
  });

  return null;
};

const PortalUserNotificationsListener: FC = () => {
  const auth = useAuth();

  if (auth.user?.id) {
    return <AuthenticatedListener userId={auth.user.id} />;
  }

  return null;
};

export default PortalUserNotificationsListener;
